// Story 16.5 — el admin mueve un bot a otra organización. Con el bot se van
// sus conversaciones y datos; desde ese momento usa las credenciales y la
// gente de la organización nueva.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Can } from '@/components/auth/Can';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { BotRowSummary } from '@/hooks/queries/use-bots';
import { useOrganizations } from '@/hooks/queries/use-organizations';
import { api } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

function TransferForm({ bot }: { bot: BotRowSummary }): React.ReactElement {
  const organizations = useOrganizations();
  const [target, setTarget] = useState('');
  const qc = useQueryClient();
  const move = useMutation({
    mutationFn: (organizationId: number) => api.put(`/api/v1/bots/${bot.id}`, { organizationId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.bots() });
      void qc.invalidateQueries({ queryKey: queryKeys.organizations() });
    },
  });
  const options = (organizations.data ?? []).filter((o) => o.id !== bot.organizationId);

  async function handleMove(): Promise<void> {
    const destination = options.find((o) => String(o.id) === target);
    if (!destination) return;
    const ok = window.confirm(
      `¿Mover ${bot.name} a ${destination.name}? Usará sus credenciales y solo lo verá su gente.`,
    );
    if (!ok) return;
    try {
      await move.mutateAsync(destination.id);
      toast.success(`${bot.name} ahora es de ${destination.name}`);
      setTarget('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo mover el bot');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ArrowRightLeft className="h-4 w-4" aria-hidden />
          Mover a otra organización
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          aria-label="Organización destino"
          className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        >
          <option value="">Elige la organización</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <Button
          variant="outline"
          disabled={!target || move.isPending}
          onClick={() => void handleMove()}
        >
          Mover
        </Button>
      </CardContent>
    </Card>
  );
}

export function TransferBotCard({ bot }: { bot: BotRowSummary }): React.ReactElement {
  return (
    <Can capability="manage_users">
      <TransferForm bot={bot} />
    </Can>
  );
}
